import { useCallback } from 'react';
import { Stack, Text, Group, Box, Button } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import classes from './DynamicConfirmationModal.module.css';

interface DynamicConfirmationModalProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  iconType?: 'warning' | 'info';
  isProcessing?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const DynamicConfirmationModal = ({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  iconType = 'warning',
  isProcessing = false,
  onConfirm,
  onCancel
}: DynamicConfirmationModalProps) => {

  const handleConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isProcessing) {
      event.preventDefault();
      handleConfirm();
    } else if (event.key === 'Escape' && !isProcessing) {
      event.preventDefault();
      handleCancel();
    }
  }, [handleConfirm, handleCancel, isProcessing]);

  return (
    <Box
      className={classes.modal}
      onKeyDown={handleKeyDown}
    >
      <Stack gap={18} align="center">
        {/* Icon */}
        <div className={classes.iconContainer}>
          {iconType === 'warning' ? (
            <IconAlertTriangle size={24} className={classes.warningIcon} />
          ) : (
            <IconInfoCircle size={24} className={classes.infoIcon} />
          )}
        </div>

        {/* Title */}
        {title && (
          <Text fw={600} className={classes.title}>
            {title}
          </Text>
        )}

        {/* Message */}
        <Text className={classes.message}>
          {message}
        </Text>

        {/* Action Buttons */}
        <Group gap={18} justify="stretch" className={classes.buttonGroup}>
          <Button
            variant="subtle"
            onClick={handleCancel}
            className={classes.cancelButton}
            data-autofocus={false}
            disabled={isProcessing}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={handleConfirm}
            className={iconType === 'warning' ? classes.deleteButton : classes.confirmButton}
            loading={isProcessing}
            disabled={isProcessing}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};

export type { DynamicConfirmationModalProps };
